import React from 'react';
import { ScreenshotProvider } from 'contexts/screenshot';
import { store } from 'ducks';
import InitializePlanner from 'initialize/InitializePlanner';
import { Provider } from 'react-redux';
import { HashRouter as Router } from 'react-router-dom';
import { ThemeProvider } from 'styled-components';
import { Normalize } from 'styled-normalize';
import theme from 'ui/theme';
import GlobalStyles from './GlobalStyles';
import I18NProvider from 'i18n/I18NProvider';
import HTML5Backend from 'react-dnd-html5-backend';
import { DndProvider } from 'react-dnd';
import Debugger from 'debug/Debugger';
import { PlannerConfigProvider } from 'contexts/plannerConfig';
import { BehaviorProvider } from 'contexts/behaviors';
import { NavigationBlockProvider } from 'contexts/navigationBlock';
import { CookieConsentProvider } from 'contexts/cookieConsent';
import InitializeAnalytics from 'analytics/InitializeAnalytics';
import LoadingScreen from 'views/LoadingScreen';
import { CoreProviderWithInsigths as CoreProvider } from 'contexts/core';
import Main from './Main';
import Helmet from 'components/Helmet/Helmet';
import OptimizelyProvider from 'optimizely/OptimizelyProvider';
import { InsightsMLProvider } from 'contexts/insightsML';
import { AvailabilityProvider } from 'contexts/availability';
import { SurveyProvider } from 'contexts/survey';
import VirtualKeyboardProvider from 'contexts/virtualKeyboard';
import { SceneHandlerProvider } from 'contexts/sceneHandler';
import { ServiceSettingsProvider } from 'contexts/serviceSettings';
import { QueryClient, QueryCache, QueryClientProvider } from '@tanstack/react-query';
import useSetHtmlLanguageAttributes from 'utils/htmlLanguageAttributes';
import Compose from 'utils/Compose';
import ErrorBoundary from 'components/ErrorBoundary/ErrorBoundary';
import { ServicesProvider } from 'contexts/services';
import useAccessibleLinks from 'hooks/useAccessibleLinks';

const App = () => {
  const cache = new QueryCache({});
  const client = new QueryClient({ queryCache: cache });

  useSetHtmlLanguageAttributes();
  useAccessibleLinks();

  return (
    <Router>
      <Normalize />
      <GlobalStyles />
      <Compose
        components={[
          [QueryClientProvider, { client }],
          [ThemeProvider, { theme }],
          [Provider, { store }],
          ErrorBoundary,
          ServiceSettingsProvider,
          ServicesProvider,
          I18NProvider,
          CoreProvider,
          CookieConsentProvider,
          OptimizelyProvider,
          InitializeAnalytics,
          InsightsMLProvider,
          ScreenshotProvider,
          PlannerConfigProvider,
          BehaviorProvider,
          SurveyProvider,
          NavigationBlockProvider,
          [DndProvider, { backend: HTML5Backend }],
          VirtualKeyboardProvider,
          AvailabilityProvider,
          InitializePlanner,
          Debugger,
          SceneHandlerProvider
        ]}
      >
        <Helmet />
        <LoadingScreen />
        <Main />
      </Compose>
    </Router>
  );
};

export default App;
